import { h, escapeHtml, register, registerFieldType } from './core.js';
import { Review } from './review.js';
import { renderIcon } from './icons.js';
import { RichText } from './richtext.js';
import { toDocx, PAGE_SIZES } from './docx.js';

/**
 * Document: RichText in page mode for long-form documents (procedures,
 * policies, reports). Adds a paged canvas with margins, header and footer
 * bands with fields, tables, a generated table of contents, page breaks,
 * find and replace, a status bar, print, and Word (.docx) export.
 *
 * new dfc.Document(container, {
 *   ...RichText options (toolbar defaults to 'document'),
 *   pageSize: 'A4' | 'Letter', margins: { top: 20, right: 20, bottom: 20, left: 20 } (mm), zoom: 1,
 *   header: '<p>{{title}}</p>', footer: '<p>{{title}} · v{{version}} · Page {{page}} of {{pages}}</p>',
 *   meta: { title, author, version, date, ...any },   // substituted into header/footer fields
 *   statusBar: true, height: null (px; page area scrolls)
 * })
 * Extra toolbar items: table, toc, pagebreak, hr, find, print, docx, meta
 * Methods: insertTable(rows, cols), refreshToc(), find(text), toDocx() -> Promise<Blob>, print(), toHtmlDocument() (standalone HTML)
 * Events: everything RichText emits, plus export { format, blob }, metaChanged { meta }
 */
const DOC_TOOLBAR = ['undo', 'redo', '|', 'heading', 'bold', 'italic', 'underline', 'strike', 'color', '|', 'alignLeft', 'alignCenter', 'alignRight', 'ul', 'ol', 'outdent', 'indent', '|', 'link', 'image', 'table', 'toc', 'pagebreak', 'hr', '|', 'find', 'clear', 'source', '|', 'comment', 'track', 'review', '|', 'meta', 'print', 'docx'];

const EXTRA_ITEMS = {
	table: { icon: 'table', title: 'Insert table' },
	toc: { icon: 'toc', title: 'Table of contents' },
	pagebreak: { icon: 'page-break', title: 'Page break' },
	hr: { icon: 'minus', title: 'Horizontal rule' },
	find: { icon: 'search', title: 'Find and replace (Ctrl+F)' },
	print: { icon: 'print', title: 'Print' },
	docx: { icon: 'file-word', title: 'Export to Word' },
	meta: { icon: 'info', title: 'Document properties' },
	comment: { icon: 'comment', title: 'Add comment' },
	track: { icon: 'track-changes', title: 'Track changes' },
	review: { icon: 'review', title: 'Show or hide the review panel' },
};

export class Document extends RichText {
	static get componentName() { return 'document'; }

	defaults() {
		return Object.assign(super.defaults(), {
			toolbar: 'document', pageSize: 'A4', margins: { top: 20, right: 20, bottom: 20, left: 20 }, zoom: 1,
			header: '<p>{{title}}</p>', footer: '<p>{{title}} · Version {{version}} · Page {{page}} of {{pages}}</p>',
			meta: { title: 'Untitled document', author: '', version: '1.0', date: new Date().toISOString().slice(0, 10) },
			statusBar: true, height: null, minHeight: 0, placeholder: '',
			user: null, comments: [], trackChanges: false, showReview: false,
		});
	}

	_items() {
		const t = this.options.toolbar;
		return Array.isArray(t) ? t : (t === 'document' ? DOC_TOOLBAR : super._items());
	}

	_buildToolbar() {
		// Let RichText build what it knows, then add the document items in place.
		const items = this._items();
		const known = items.map((n) => (EXTRA_ITEMS[n] ? '|' : n));
		this.options.toolbar = known; super._buildToolbar(); this.options.toolbar = items;
		const seps = [...this.toolbar.querySelectorAll('.dfc-richtext-sep')];
		let sepIdx = 0;
		items.forEach((name) => {
			if (name === '|') { sepIdx++; return; }
			if (!EXTRA_ITEMS[name]) return;
			const def = EXTRA_ITEMS[name];
			const btn = h('button', { type: 'button', className: 'dfc-richtext-btn', title: def.title, 'aria-label': def.title, dataset: { item: name } }, renderIcon(def.icon));
			const at = seps[sepIdx];
			if (at) at.replaceWith(btn); else this.toolbar.appendChild(btn);
			seps[sepIdx] = null; sepIdx++;
			this.buttons[name] = btn;
		});
	}

	render() {
		super.render();
		const o = this.options;
		this.el.classList.add('dfc-document');
		// Wrap the editor in a page canvas
		const size = PAGE_SIZES[o.pageSize] || PAGE_SIZES.A4;
		const m = Object.assign({ top: 20, right: 20, bottom: 20, left: 20 }, o.margins || {});
		this.canvas = h('div', { className: 'dfc-document-canvas', style: { height: o.height ? o.height + 'px' : null } });
		this.page = h('div', { className: 'dfc-document-page', style: { width: size.w + 'mm', minHeight: size.h + 'mm', padding: `${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm`, transform: o.zoom !== 1 ? `scale(${o.zoom})` : null } });
		this.headerEl = h('div', { className: 'dfc-document-header', style: { left: m.left + 'mm', right: m.right + 'mm' } });
		this.footerEl = h('div', { className: 'dfc-document-footer', style: { left: m.left + 'mm', right: m.right + 'mm' } });
		this.page.appendChild(this.headerEl);
		this.page.appendChild(this.editor);
		this.page.appendChild(this.source);
		this.page.appendChild(this.footerEl);
		this.canvas.appendChild(this.page);
		this.body = h('div', { className: 'dfc-document-body' }, this.canvas);
		this.el.insertBefore(this.body, this.fileInput);
		if (this.review && this.review.panel) { this.review.uninstall(); this.review.install(); this.body.appendChild(this.review.panel); }
		this.editor.style.minHeight = `calc(${size.h}mm - ${m.top + m.bottom}mm)`;
		this._paintBands();
		if (o.statusBar) {
			this.statusEl = h('div', { className: 'dfc-document-status' });
			this.el.appendChild(this.statusEl);
			this._paintStatus();
		}
	}

	bind() {
		super.bind();
		this._onDocKey = (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f' && this.buttons.find) { e.preventDefault(); this._run('find'); }
			if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); this.insertPageBreak(); }
		};
		this.editor.addEventListener('keydown', this._onDocKey);
		this._onTableKey = (e) => {
			if (e.key !== 'Tab') return;
			const cell = this._closest('td, th');
			if (!cell) return;
			e.preventDefault();
			const cells = [...cell.closest('table').querySelectorAll('td, th')];
			const i = cells.indexOf(cell) + (e.shiftKey ? -1 : 1);
			if (i >= cells.length) { this.tableAddRow(); const all = [...cell.closest('table').querySelectorAll('td, th')]; this._placeCaret(all[cells.length]); }
			else if (i >= 0) this._placeCaret(cells[i]);
		};
		this.editor.addEventListener('keydown', this._onTableKey);
		this._onEditorClick = () => this._refreshTableTools();
		this.editor.addEventListener('click', this._onEditorClick);
		this.editor.addEventListener('keyup', this._onEditorClick);
		this.review = new Review(this);
		this.review.install();
		this.body.appendChild(this.review.panel);
		this.review.syncAnchors();
	}

	unbind() { super.unbind(); if (this.review) this.review.uninstall(); if (this.editor) { this.editor.removeEventListener('keydown', this._onDocKey); this.editor.removeEventListener('keydown', this._onTableKey); } }

	_run(name) {
		if (!EXTRA_ITEMS[name]) { super._run(name); return; }
		if (this.options.readOnly || this.options.disabled) return;
		if (this._sourceMode) this.toggleSource(false);
		this._restoreSelection();
		if (name === 'table') this._openTablePopover(this.buttons.table);
		else if (name === 'toc') this.refreshToc(true);
		else if (name === 'pagebreak') this.insertPageBreak();
		else if (name === 'hr') this.exec('insertHTML', '<hr>');
		else if (name === 'find') this._openFindPopover(this.buttons.find);
		else if (name === 'print') this.print();
		else if (name === 'docx') this.downloadDocx();
		else if (name === 'meta') this._openMetaPopover(this.buttons.meta);
		else if (name === 'comment') this.review.openCommentPopover(this.buttons.comment);
		else if (name === 'track') this.review.setTracking(!this.review.tracking);
		else if (name === 'review') this.review.togglePanel();
		this.emit('command', { name });
	}

	_afterEdit(source) {
		super._afterEdit(source);
		this._paintStatus();
		this._refreshTableTools();
		if (this.review) { this.review.syncAnchors(); this.review.renderChanges(); this.emit('changesChanged', { changes: this.changes }); }
	}

	// ------------------------------------------------------------ review: comments and track changes
	/** Comment threads ({ id, text, author, at, resolved, replies }). Anchors live in the HTML as span.dfc-comment. */
	get comments() { return this.review ? this.review.list : []; }
	addComment(text) { return this.review.add(text); }
	replyComment(id, text) { this.review.reply(id, text); return this; }
	resolveComment(id, resolved = true) { this.review.resolve(id, resolved); return this; }
	removeComment(id) { this.review.remove(id); return this; }
	setActiveComment(id) { this.review.setActive(id); return this; }
	/** Tracked changes read from the HTML ({ id, type: 'insert' | 'delete', author, at, text }). */
	get changes() { return this.review ? this.review.changes.map((c) => ({ id: c.id, type: c.type, author: c.author, at: c.at, text: c.text })) : []; }
	get tracking() { return !!(this.review && this.review.tracking); }
	set tracking(on) { if (this.review) this.review.setTracking(on); }
	acceptChange(id) { this.review.acceptChange(id); return this; }
	rejectChange(id) { this.review.rejectChange(id); return this; }
	acceptAllChanges() { this.review.acceptAll(); return this; }
	rejectAllChanges() { this.review.rejectAll(); return this; }
	toggleReview(on) { this.review.togglePanel(on); return this; }
	/** HTML with every change accepted and comment anchors removed: what the reader sees, and what print and Word export use. */
	get cleanValue() {
		const tpl = document.createElement('template'); tpl.innerHTML = this.value;
		Review.clean(tpl.content);
		return tpl.innerHTML;
	}
	/** Everything needed to store and reopen the document. */
	toJSON() { return { html: this.value, comments: this.comments, meta: this.meta }; }
	/** Loads { html, comments, meta } produced by toJSON. */
	load(data) {
		const d = data || {};
		if (d.meta) this.setMeta(d.meta);
		if (this.review) { this.review.comments = (d.comments || []).map((c) => Object.assign({ replies: [], resolved: false }, c)); this.review.activeId = null; }
		this.value = d.html || '';
		if (this.review) { this.review.syncAnchors(); this.review.renderPanel(); }
		return this;
	}

	// ------------------------------------------------------------ meta, header, footer
	get meta() { return Object.assign({}, this.options.meta); }
	setMeta(patch) {
		this.options.meta = Object.assign({}, this.options.meta, patch || {});
		this._paintBands();
		this.emit('metaChanged', { meta: this.meta });
		return this;
	}
	/** Substitutes {{field}} placeholders from meta. With keepPageFields the {{page}}/{{pages}} tokens are left for the DOCX writer. */
	_fields(html, opts = {}) {
		const meta = Object.assign({ page: 1, pages: 1 }, this.options.meta || {});
		return String(html || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, k) => {
			if (opts.keepPageFields && (k === 'page' || k === 'pages')) return m;
			return escapeHtml(meta[k] !== undefined ? meta[k] : '');
		});
	}
	_paintBands() {
		if (!this.headerEl) return;
		this.headerEl.innerHTML = RichText.sanitize(this._fields(this.options.header), this._allowed(), this._sanOpts());
		this.footerEl.innerHTML = RichText.sanitize(this._fields(this.options.footer), this._allowed(), this._sanOpts());
	}

	_paintStatus() {
		if (!this.statusEl) return;
		const text = this.getText();
		const words = (text.match(/\S+/g) || []).length;
		const size = PAGE_SIZES[this.options.pageSize] || PAGE_SIZES.A4;
		const pages = Math.max(1, Math.ceil(this.editor.scrollHeight / (size.h * 3.78)) + (this.editor.querySelectorAll('.dfc-page-break').length));
		this.statusEl.textContent = `${words} word${words === 1 ? '' : 's'} · ${text.length} characters · about ${pages} page${pages === 1 ? '' : 's'} · ${this.options.pageSize}`;
	}

	// ------------------------------------------------------------ tables
	insertTable(rows = 3, cols = 3, withHeader = true) {
		let html = '<table>';
		for (let r = 0; r < rows; r++) {
			html += '<tr>';
			for (let c = 0; c < cols; c++) html += withHeader && r === 0 ? '<th><br></th>' : '<td><br></td>';
			html += '</tr>';
		}
		html += '</table><p><br></p>';
		this.exec('insertHTML', html);
		return this;
	}
	_closest(sel) {
		const s = window.getSelection();
		if (!s || !s.rangeCount) return null;
		let n = s.getRangeAt(0).commonAncestorContainer; if (n.nodeType === 3) n = n.parentNode;
		const el = n.closest && n.closest(sel);
		return el && this.editor.contains(el) ? el : null;
	}
	_placeCaret(el) {
		if (!el) return;
		const r = document.createRange(); r.selectNodeContents(el); r.collapse(true);
		const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); this._saveSelection();
	}
	tableAddRow(before) {
		const cell = this._closest('td, th'); if (!cell) return this;
		const row = cell.parentNode; const clone = row.cloneNode(true);
		[...clone.children].forEach((c) => { c.innerHTML = '<br>'; if (c.tagName === 'TH') { const td = document.createElement('td'); td.innerHTML = '<br>'; c.replaceWith(td); } });
		row.parentNode.insertBefore(clone, before ? row : row.nextSibling); this._afterEdit('user'); return this;
	}
	tableAddColumn(before) {
		const cell = this._closest('td, th'); if (!cell) return this;
		const idx = [...cell.parentNode.children].indexOf(cell);
		[...cell.closest('table').querySelectorAll('tr')].forEach((tr) => { const ref = tr.children[idx]; const n = document.createElement(ref && ref.tagName === 'TH' ? 'th' : 'td'); n.innerHTML = '<br>'; if (ref) tr.insertBefore(n, before ? ref : ref.nextSibling); else tr.appendChild(n); });
		this._afterEdit('user'); return this;
	}
	tableDeleteRow() { const cell = this._closest('td, th'); if (!cell) return this; const tr = cell.parentNode; const table = cell.closest('table'); tr.remove(); if (!table.querySelector('tr')) table.remove(); this._afterEdit('user'); return this; }
	tableDeleteColumn() { const cell = this._closest('td, th'); if (!cell) return this; const idx = [...cell.parentNode.children].indexOf(cell); const table = cell.closest('table'); [...table.querySelectorAll('tr')].forEach((tr) => { if (tr.children[idx]) tr.children[idx].remove(); }); if (!table.querySelector('td, th')) table.remove(); this._afterEdit('user'); return this; }
	tableDelete() { const t = this._closest('table'); if (t) { t.remove(); this._afterEdit('user'); } return this; }
	_refreshTableTools() {
		const inTable = !!this._closest('table');
		if (inTable && !this.tableTools) {
			this.tableTools = h('div', { className: 'dfc-document-tabletools' },
				...[['Row above', () => this.tableAddRow(true)], ['Row below', () => this.tableAddRow(false)], ['Column left', () => this.tableAddColumn(true)], ['Column right', () => this.tableAddColumn(false)], ['Delete row', () => this.tableDeleteRow()], ['Delete column', () => this.tableDeleteColumn()], ['Delete table', () => this.tableDelete()]]
					.map(([label, fn]) => h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: label, onMousedown: (e) => e.preventDefault(), onClick: fn })));
			this.toolbar.appendChild(this.tableTools);
		} else if (!inTable && this.tableTools) { this.tableTools.remove(); this.tableTools = null; }
	}
	_openTablePopover(anchor) {
		this.popover.innerHTML = ''; this.popover.hidden = false; this.popover.style.left = Math.max(0, anchor.offsetLeft) + 'px';
		const rows = h('input', { type: 'number', min: '1', max: '50', value: '3', className: 'dfc-richtext-linkinput', style: { minWidth: '70px' }, 'aria-label': 'Rows' });
		const cols = h('input', { type: 'number', min: '1', max: '12', value: '3', className: 'dfc-richtext-linkinput', style: { minWidth: '70px' }, 'aria-label': 'Columns' });
		const head = h('input', { type: 'checkbox', checked: true, id: this.id + '-th' });
		this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, h('span', { text: 'Rows' }), rows, h('span', { text: 'Columns' }), cols));
		this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, head, h('label', { for: this.id + '-th', text: 'Header row' }), h('button', { type: 'button', className: 'dfc-btn', text: 'Insert', onClick: () => { this._restoreSelection(); this.insertTable(Number(rows.value) || 3, Number(cols.value) || 3, head.checked); this._closePopover(); } })));
	}

	// ------------------------------------------------------------ TOC, page breaks
	refreshToc(insertIfMissing) {
		const heads = [...this.editor.querySelectorAll('h1, h2, h3')].filter((x) => !x.closest('.dfc-toc'));
		heads.forEach((hd, i) => { if (!hd.id) hd.id = 'sec-' + (i + 1) + '-' + (hd.textContent || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40); });
		let toc = this.editor.querySelector('.dfc-toc');
		if (!toc) { if (!insertIfMissing) return this; toc = document.createElement('nav'); toc.className = 'dfc-toc'; }
		let html = '<p class="dfc-toc-title"><b>Contents</b></p><ol>';
		heads.forEach((hd) => { const lvl = Number(hd.tagName[1]); html += `<li class="dfc-toc-l${lvl}"><a href="#${hd.id}">${escapeHtml(hd.textContent)}</a></li>`; });
		toc.innerHTML = html + '</ol>';
		if (!toc.parentNode) { this._restoreSelection(); this.exec('insertHTML', toc.outerHTML); } else this._afterEdit('user');
		return this;
	}
	insertPageBreak() { return this.exec('insertHTML', '<hr class="dfc-page-break"><p><br></p>'); }

	// ------------------------------------------------------------ find & replace
	_openFindPopover(anchor) {
		this.popover.innerHTML = ''; this.popover.hidden = false; this.popover.style.left = Math.max(0, anchor.offsetLeft) + 'px';
		const find = h('input', { type: 'text', className: 'dfc-richtext-linkinput', placeholder: 'Find', 'aria-label': 'Find' });
		const repl = h('input', { type: 'text', className: 'dfc-richtext-linkinput', placeholder: 'Replace with', 'aria-label': 'Replace with' });
		const count = h('span', { className: 'dfc-document-findcount' });
		let matches = [], idx = -1;
		const clear = () => { [...this.editor.querySelectorAll('mark.dfc-find')].forEach((m) => { m.replaceWith(document.createTextNode(m.textContent)); }); this.editor.normalize(); matches = []; idx = -1; };
		const highlight = () => {
			clear();
			const q = find.value; if (!q) { count.textContent = ''; return; }
			const walker = document.createTreeWalker(this.editor, NodeFilter.SHOW_TEXT);
			const nodes = []; let n; while ((n = walker.nextNode())) nodes.push(n);
			nodes.forEach((t) => {
				let text = t.nodeValue, pos = text.toLowerCase().indexOf(q.toLowerCase()); if (pos < 0) return;
				const frag = document.createDocumentFragment(); let last = 0;
				while (pos >= 0) { frag.appendChild(document.createTextNode(text.slice(last, pos))); const mk = document.createElement('mark'); mk.className = 'dfc-find'; mk.textContent = text.slice(pos, pos + q.length); frag.appendChild(mk); matches.push(mk); last = pos + q.length; pos = text.toLowerCase().indexOf(q.toLowerCase(), last); }
				frag.appendChild(document.createTextNode(text.slice(last))); t.replaceWith(frag);
			});
			count.textContent = matches.length ? `${matches.length} found` : 'No matches';
			next();
		};
		const next = () => { if (!matches.length) return; idx = (idx + 1) % matches.length; matches.forEach((m, i) => m.classList.toggle('is-current', i === idx)); matches[idx].scrollIntoView({ block: 'center' }); };
		const replaceOne = () => { if (idx < 0 || !matches[idx]) return; matches[idx].replaceWith(document.createTextNode(repl.value)); this.editor.normalize(); this._afterEdit('user'); highlight(); };
		const replaceAll = () => { matches.forEach((m) => m.replaceWith(document.createTextNode(repl.value))); this.editor.normalize(); this._afterEdit('user'); highlight(); };
		find.addEventListener('input', highlight);
		find.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); next(); } if (e.key === 'Escape') { clear(); this._closePopover(); } });
		this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, find, h('button', { type: 'button', className: 'dfc-btn', text: 'Next', onClick: next }), count));
		this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, repl, h('button', { type: 'button', className: 'dfc-btn', text: 'Replace', onClick: replaceOne }), h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: 'Replace all', onClick: replaceAll }), h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: 'Close', onClick: () => { clear(); this._closePopover(); } })));
		setTimeout(() => find.focus(), 0);
	}
	_closePopover() { if (this.editor) { [...this.editor.querySelectorAll('mark.dfc-find')].forEach((m) => m.replaceWith(document.createTextNode(m.textContent))); this.editor.normalize(); } super._closePopover(); }

	// ------------------------------------------------------------ properties
	_openMetaPopover(anchor) {
		this.popover.innerHTML = ''; this.popover.hidden = false; this.popover.style.left = Math.max(0, anchor.offsetLeft) + 'px';
		const fields = ['title', 'author', 'version', 'date'];
		const inputs = {};
		fields.forEach((f) => { inputs[f] = h('input', { type: f === 'date' ? 'date' : 'text', className: 'dfc-richtext-linkinput', value: this.options.meta[f] || '', 'aria-label': f }); this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, h('span', { text: f[0].toUpperCase() + f.slice(1), style: { width: '70px' } }), inputs[f])); });
		this.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, h('button', { type: 'button', className: 'dfc-btn', text: 'Apply', onClick: () => { const patch = {}; fields.forEach((f) => { patch[f] = inputs[f].value; }); this.setMeta(patch); this._closePopover(); } })));
	}

	// ------------------------------------------------------------ export + print
	/** Standalone HTML document (own styles, header/footer text, page CSS). */
	toHtmlDocument() {
		const o = this.options; const size = PAGE_SIZES[o.pageSize] || PAGE_SIZES.A4; const m = o.margins;
		return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(o.meta.title || '')}</title><style>
@page { size: ${o.pageSize}; margin: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm; }
body { font: 11pt/1.5 Calibri, "Segoe UI", Arial, sans-serif; color: #1f2937; margin: 0; }
h1 { font-size: 18pt; } h2 { font-size: 15pt; } h3 { font-size: 13pt; } p, li { margin: 0 0 6pt; }
table { border-collapse: collapse; width: 100%; margin: 0 0 8pt; } td, th { border: 1px solid #999; padding: 4pt 6pt; vertical-align: top; } th { background: #eef1f4; text-align: left; }
img { max-width: 100%; } hr.dfc-page-break { page-break-after: always; border: 0; height: 0; margin: 0; } blockquote { border-left: 3px solid #999; margin: 0 0 6pt; padding-left: 8pt; color: #6b7280; }
.dfc-toc ol { list-style: none; padding-left: 0; } .dfc-toc .dfc-toc-l2 { padding-left: 16pt; } .dfc-toc .dfc-toc-l3 { padding-left: 32pt; } .dfc-toc a { color: inherit; text-decoration: none; }
.doc-header, .doc-footer { position: fixed; left: 0; right: 0; font-size: 9pt; color: #6b7280; } .doc-header { top: 4mm; } .doc-footer { bottom: 4mm; }
@media screen { body { padding: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm; max-width: ${size.w}mm; margin: 0 auto; } .doc-header, .doc-footer { position: static; } }
</style></head><body><div class="doc-header">${RichText.sanitize(this._fields(o.header), this._allowed(), this._sanOpts())}</div>${this.cleanValue}<div class="doc-footer">${RichText.sanitize(this._fields(o.footer), this._allowed(), this._sanOpts())}</div></body></html>`;
	}
	print() {
		const frame = h('iframe', { style: { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' }, 'aria-hidden': 'true' });
		document.body.appendChild(frame);
		const doc = frame.contentDocument; doc.open(); doc.write(this.toHtmlDocument()); doc.close();
		const done = () => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } finally { setTimeout(() => frame.remove(), 1000); } };
		if (doc.readyState === 'complete') setTimeout(done, 50); else frame.onload = done;
		return this;
	}
	async toDocx() {
		const o = this.options;
		const blob = await toDocx(this.cleanValue, { title: o.meta.title, author: o.meta.author, pageSize: o.pageSize, margins: o.margins, headerHtml: this._fields(o.header, { keepPageFields: true }), footerHtml: this._fields(o.footer, { keepPageFields: true }) });
		this.emit('export', { format: 'docx', blob });
		return blob;
	}
	async downloadDocx(fileName) {
		const blob = await this.toDocx();
		const name = fileName || ((this.options.meta.title || 'document').replace(/[^\w.-]+/g, '_') + '.docx');
		const a = h('a', { href: URL.createObjectURL(blob), download: name }); document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
		return blob;
	}
}

register('document', Document);
registerFieldType('document', {
	create: (container, field, value) => new Document(container, { value: value || '', readOnly: !!field.readonly, meta: field.meta || undefined, user: field.user || undefined }),
	getValue: (inst) => inst.value,
	setValue: (inst, v) => { inst.value = v; },
});
